import React, { useEffect, useRef } from 'react';
import { postApi } from '../../Services/apiService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useNotifications } from '../../Context/NotificationContext';

const CellRemarkNotifications = () => {
  const {
    notifications,
    unreadCount,
    readCount,
    loading,
    hasMore,
    activeTab,
    changeTab,
    loadMoreNotifications,
    refreshNotifications,
    prevUnreadCountRef,
    isInitialMount,
    isNotificationOpen: isOpen,
    setIsNotificationOpen: setIsOpen
  } = useNotifications();


  const navigate = useNavigate();
  const loadMoreTriggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Show toast notification when new unread notifications arrive
  useEffect(() => {
    if (!isInitialMount.current && prevUnreadCountRef.current !== null) {
      const newNotifications = unreadCount - prevUnreadCountRef.current;
      if (newNotifications > 0) {
        toast.info(
          `You have ${newNotifications} new remark${newNotifications > 1 ? 's' : ''}!`,
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    }
  }, [unreadCount, prevUnreadCountRef, isInitialMount]);

  // Infinite scroll implementation using IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          loadMoreNotifications();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    const currentTrigger = loadMoreTriggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [hasMore, loading, loadMoreNotifications]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const handleNotificationClick = async (notification) => {
    // Mark as read if it's unread
    if (!notification.isRead) {
      try {
        await postApi(`/cell-remark/${notification.id}/mark-as-read`);
        // Refresh notifications to update counts
        refreshNotifications();
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    
    // Navigate to the appropriate page based on module
    const params = new URLSearchParams();
    params.append('date', notification.date);
    params.append('cell_type', notification.cell_type);
    
    // Add all cell identification params
    if (notification.product_id) {
      params.append('product_id', notification.product_id);
    }
    if (notification.product_group_id) {
      params.append('product_group_id', notification.product_group_id);
    }
    if (notification.finished_product_id) {
      params.append('finished_product_id', notification.finished_product_id);
    }
    if (notification.raw_product_id) {
      params.append('raw_product_id', notification.raw_product_id);
    }
    if (notification.customer_group_id) {
      params.append('customer_group_id', notification.customer_group_id);
    }
    if (notification.customer_sub_group_id) {
      params.append('customer_sub_group_id', notification.customer_sub_group_id);
    }
    if (notification.reserve_label) {
      params.append('reserve_label', notification.reserve_label);
    }
    if (notification.arrival_index !== null && notification.arrival_index !== undefined) {
      params.append('arrival_index', notification.arrival_index);
    }
    
    // Determine navigation path based on module
    const navigationPath = notification.module === 'inout' 
      ? '/admin/inout' 
      : '/admin/prepack-inout';
    
    navigate(`${navigationPath}?${params.toString()}`);
    setIsOpen(false);
  };

  const handleTabChange = (tab) => {
    changeTab(tab);
  };

  const getCellTypeLabel = (cellType) => {
    const labels = {
      'arrival_info': 'Arrival Info',
      'sales_reserve': 'Sales Reserve',
      'arrival_qty': 'Arrival Qty',
      'total_qty': 'Total Qty',
      'estimate_stock': 'Estimate Stock',
      'convert_in': 'Convert In',
      'convert_out': 'Convert Out',
    };
    return labels[cellType] || cellType;
  };

  const getProductDisplay = (notification) => {
    // For product groups (both inout and prepack modules)
    if (notification.product_group) {
      return `Group: ${notification.product_group.group_no} ${notification.product_group.name || ''}`;
    }
    // For products (both inout and prepack modules)
    if (notification.product) {
      return `${notification.product.code} - ${notification.product.name_tc}`;
    } 
  };

  return (
    <div className="cell-remark-notifications" ref={dropdownRef}>
      <button 
        className="notification-bell right-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        <img
                src={window.getPublicUrl("/assets/images/notification.svg")}
                alt="icon"
              />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>
       

      {isOpen && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <h4>Cell Remarks</h4>
          </div>

          {/* Tabs */}
          <div className="notifications-tabs">
            <button 
              className={`tab-button ${activeTab === 'unread' ? 'active' : ''}`}
              onClick={() => handleTabChange('unread')}
            >
              Unread ({unreadCount})
            </button>
            <button 
              className={`tab-button ${activeTab === 'read' ? 'active' : ''}`}
              onClick={() => handleTabChange('read')}
            >
              Read ({readCount})
            </button>
            <button 
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => handleTabChange('all')}
            >
              All ({unreadCount + readCount})
            </button>
          </div>

          <div className="notifications-body">
            {loading ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                No {activeTab === 'unread' ? 'unread' : activeTab === 'read' ? 'read' : ''} remarks
              </div>
            ) : (
              <>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-header-item">
                      <span className="notification-type">
                        {getCellTypeLabel(notification.cell_type)}
                      </span>
                      <span className="notification-date">
                        {notification.date}
                      </span>
                    </div>
                    <div className="notification-product">
                      {getProductDisplay(notification)}
                    </div>
                    <div className="notification-remark">
                      {notification.remark.substring(0, 100)}
                      {notification.remark.length > 100 && '...'}
                    </div>
                    <div className="notification-meta">
                      <span>By: {notification.creator?.name}</span>
                      <span className="notification-time">
                        {new Date(notification.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
                
                {/* Infinite Scroll Trigger */}
                {hasMore && (
                  <div 
                    ref={loadMoreTriggerRef} 
                    className="notifications-load-trigger"
                  >
                    {loading && (
                      <div className="notification-loading-more">
                        <div className="spinner"></div>
                        <span>Loading more...</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CellRemarkNotifications;
